/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { History, Fingerprint, User, FileText, ShieldCheck, Pill, ScanEye } from 'lucide-react';
import { getMyAudit } from '@/lib/security';

const ICONS = {
  'albo.verify': ShieldCheck,
  '2fa.enabled': Fingerprint,
  '2fa.setup_started': Fingerprint,
  '2fa.disabled': Fingerprint,
  'consent.update': ShieldCheck,
  'prescription.create': Pill,
  'prescription.view': ScanEye,
  'document.upload': FileText,
  'document.delete': FileText,
};

export const AuditLogViewer = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyAudit(200).then((r) => setItems(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="audit-log-card">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2"><History className="h-5 w-5 text-primary" /> {t('auditTitle')}</CardTitle>
        <CardDescription>{t('auditSubtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <div className="text-sm text-muted-foreground">{t('loading')}</div> :
         items.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t('auditEmpty')}</div> :
         <div className="space-y-2 max-h-[420px] overflow-y-auto scrollbar-thin">
           {items.map((e) => {
             const Icon = ICONS[e.action] || User;
             const ip = (e.meta || {}).ip || '—';
             return (
               <div key={e.id} className="flex items-start gap-3 rounded-xl border border-border/60 p-2.5" data-testid={`audit-row-${e.id}`}>
                 <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon className="h-4 w-4" /></div>
                 <div className="flex-1 min-w-0">
                   <div className="flex items-center gap-2 flex-wrap">
                     <span className="font-semibold text-sm">{t(`auditAction_${e.action}`, e.action)}</span>
                     <Badge variant="outline" className="text-[10px]">{e.target_type}</Badge>
                   </div>
                   <div className="text-[11px] text-muted-foreground mt-0.5">{new Date(e.at).toLocaleString('it')} · IP: {ip}</div>
                 </div>
               </div>
             );
           })}
         </div>
        }
      </CardContent>
    </Card>
  );
};

export default AuditLogViewer;
